import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import {
  Divider,
  Grid,
  IconButton,
  Input,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import {
  Add as CreateIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Done as SaveIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  NorthEast as NorthEastIcon,
  SouthWest as SouthWestIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

import { Table } from '../../components/table/table';
import { FeeHistoryModal } from './components';
import { HeadCell, Row, RowCell } from '../../components/table/types';

import { UTCToDateTime } from '../../utils/dates';
import { useAppActions } from '../../utils/hooks';
import { Option, TransactionDirection } from '../../enums';
import { layoutAction } from '../../store/sideBarStore/layoutStore';
import { PutFeeDTO, useDeleteFeeMutation, useGetFeesQuery, usePutFeeMutation } from '../../api';
import type { Fee, ProviderFee } from '../../types';
import { Link, useParams } from 'react-router-dom';
import { profile } from 'console';
import { RouterPaths } from '../../router/paths';
import {
  usePutProviderFeesMutation,
  usePostProviderFeeMutation,
  useDeleteProviderFeeMutation,
  useGetProviderFeesQuery,
  PostProviderFeeDTO,
} from '../../api/controllers/providers';
import { CreateProviderFeeModal } from './components/CreateProviderFeeModal';
import { DeleteProviderFeeModal } from './components/DeleteProviderFeeModal';
import { permissionsCheck } from '../../utils/permission';
import { formatAmount } from '../../utils';

export const ProviderFees = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { provider } = useParams<'provider'>();
  const hasPermissionToEditProviderFees = permissionsCheck(['WRITE_EXCHANGE_PROVIDER_FEES']);

  // api
  const {
    data: feeList,
    isLoading: isFeeLoading,
    refetch,
  } = useGetProviderFeesQuery({
    provider: provider ?? '',
  });
  const [putFee, { isLoading: isFeePutLoading }] = usePutProviderFeesMutation();
  const [postFee, { isLoading: isFeeCreateLoading }] = usePostProviderFeeMutation();
  const [deleteFee, { isLoading: isFeeDeleteLoading }] = useDeleteProviderFeeMutation();

  // variables
  const isLoading = isFeeLoading || isFeePutLoading || isFeeDeleteLoading || isFeeCreateLoading;

  // edit mode logic
  const [activeRowId, setActiveRowId] = useState<number | null>(null);
  const [isEditMode, setEditMode] = useState(false);
  const startEditMode = (fee: ProviderFee) => {
    setEditFields(fee as unknown as PostProviderFeeDTO['body']);
    setActiveRowId(fee.id);
    setEditMode(true);
  };
  const offEditMode = () => {
    setEditFields(null);
    setActiveRowId(null);
    setEditMode(false);
  };

  // show action button
  const { setSpeedDialButtonComponent, removeSpeedDialButtonContent } = useAppActions(layoutAction);
  useEffect(() => {
    hasPermissionToEditProviderFees &&
      setSpeedDialButtonComponent([
        { icon: <CreateIcon />, onClick: openCreateModal, disabled: isEditMode },
      ]);

    return () => {
      removeSpeedDialButtonContent();
    };
  }, [isEditMode]);

  // edit fields logic
  const [editFields, setEditFields] = useState<PostProviderFeeDTO['body'] | null>(null);
  const handleEditFields = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const name = e.target.name;
    const value = e.target.value;

    // @ts-ignore
    setEditFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // fee history modal logic
  const [historyModalShown, setHistoryModalShown] = useState(false);
  const openHistoryModal = (id: number) => {
    setHistoryModalShown(true);
    setActiveRowId(id);
  };
  const closeHistoryModal = () => {
    setHistoryModalShown(false);
    setActiveRowId(null);
  };

  // add fee modal logic
  const [createModalShown, setCreateModalShown] = useState(false);
  const openCreateModal = () => {
    setCreateModalShown(true);
    // setEditFields(null);
  };
  const closeCreateModal = () => {
    setCreateModalShown(false);
    setEditFields(null);
  };

  // delete fee modal logic
  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const openDeleteModal = (fee: ProviderFee) => {
    setDeleteModalShown(true);
    setActiveRowId(fee.id);
  };
  const closeDeleteModal = () => {
    setDeleteModalShown(false);
    setActiveRowId(null);
  };

  // edit fee handler
  const editFee = async () => {
    if (!activeRowId || !editFields || !provider) return;

    const args = {
      provider,
      fee_id: activeRowId,
      body: {
        coin_id: editFields.coin_id,
        fixed_amount: String(editFields.fixed_amount),
        percent: String(editFields.percent),
        option: editFields.option,
      },
    };

    try {
      await putFee(args).unwrap();
      offEditMode();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // create fee handler
  const createFee = async () => {
    if (!editFields || !provider) return;

    const args = {
      provider,
      body: {
        coin_id: editFields.coin_id,
        fixed_amount: String(editFields.fixed_amount),
        percent: String(editFields.percent),
        option: editFields.option,
      },
    };

    try {
      await postFee(args).unwrap();
      closeCreateModal();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // delete fee handler
  const deleteFeeHandler = async () => {
    if (!activeRowId || !provider) return;

    const args = { provider, fee_id: activeRowId };

    try {
      await deleteFee(args).unwrap();
      closeDeleteModal();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('provider-fees.head-cells.coin') },
    { id: '2', label: t('provider-fees.head-cells.provider') },
    { id: '3', label: t('provider-fees.head-cells.fixed-amount'), align: 'right' },
    { id: '4', label: t('provider-fees.head-cells.option') },
    { id: '5', label: t('provider-fees.head-cells.percent'), align: 'right' },
    { id: '6', label: t('provider-fees.head-cells.row-start') },
    {
      id: '7',
      label: t('provider-fees.head-cells.actions'),
      hidden: !hasPermissionToEditProviderFees,
    },
  ];

  const rows: Row[] | undefined = feeList?.providerFees.map((it) => {
    const { id, coin, provider, fixed_amount, option, percent, row_start } = it;

    const cells: RowCell[] = [
      { name: 'coin_name', value: coin.name },
      {
        name: 'provider',
        value: provider,
      },
      {
        name: 'fixed_amount',
        value: formatAmount(fixed_amount),
        align: 'right',
        component:
          isEditMode && activeRowId === id ? (
            <Input
              name="fixed_amount"
              type="number"
              value={editFields?.fixed_amount ?? ''}
              onChange={(e) => handleEditFields(e)}
              inputProps={{ min: 0, step: '0.01' }}
              sx={{ maxWidth: '100px' }}
              disabled={isFeePutLoading}
            />
          ) : undefined,
      },
      {
        name: 'option',
        value: option,
        component:
          isEditMode && activeRowId === id ? (
            <Select
              name="option"
              value={editFields?.option ?? ''}
              onChange={(e) => handleEditFields(e)}
              variant="standard"
              sx={{ width: '80px' }}
              disabled={isFeePutLoading}
            >
              {Object.values(Option).map((it) => {
                return (
                  <MenuItem key={it} value={it}>
                    {it}
                  </MenuItem>
                );
              })}
            </Select>
          ) : undefined,
      },
      {
        name: 'percent',
        value: percent,
        align: 'right',
        formatting: (value: string) => {
          return `${value} %`;
        },
        component:
          isEditMode && activeRowId === id ? (
            <Input
              name="percent"
              type="number"
              value={editFields?.percent ?? ''}
              onChange={handleEditFields}
              inputProps={{ min: 0, step: '0.01' }}
              endAdornment="%"
              sx={{ maxWidth: '100px' }}
              disabled={isFeePutLoading}
            />
          ) : undefined,
      },
      {
        name: 'row_start',
        value: row_start,
        formatting: (value: string) => {
          return UTCToDateTime(value);
        },
      },
      {
        name: 'actions',
        hidden: !hasPermissionToEditProviderFees,
        component: isEditMode ? (
          activeRowId === id ? (
            <Stack direction="row" spacing={2}>
              <IconButton
                aria-label="save"
                color="success"
                onClick={() => editFee()}
                disabled={isFeePutLoading}
              >
                <SaveIcon />
              </IconButton>

              <IconButton
                aria-label="cancel"
                color="error"
                onClick={() => offEditMode()}
                disabled={isFeePutLoading}
              >
                <CancelIcon />
              </IconButton>
            </Stack>
          ) : undefined
        ) : (
          <Stack direction="row" spacing={2}>
            <IconButton
              aria-label="show-history"
              color="info"
              onClick={() => openHistoryModal(id)}
              disabled={isLoading}
            >
              <HistoryIcon />
            </IconButton>

            <IconButton
              aria-label="edit"
              color="warning"
              onClick={() => startEditMode(it)}
              disabled={isLoading}
            >
              <EditIcon />
            </IconButton>

            <IconButton
              aria-label="delete"
              color="error"
              onClick={() => openDeleteModal(it)}
              disabled={isLoading}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ];

    return { cells };
  });

  return (
    <>
      <Stack direction="row" alignItems="flex-end" spacing={2} pl={3} pt={1}>
        <MuiLink underline="hover" component={Link} to={RouterPaths.ProviderFees}>
          <Stack direction="row" alignItems="stretch" spacing={0.2}>
            <ArrowBackIcon fontSize="inherit" />
            <Typography variant="caption" component="span">
              {t('providers-fee.back-to-groups')}
            </Typography>
          </Stack>
        </MuiLink>

        <Divider orientation="vertical" />
      </Stack>
      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
      </Grid>

      <FeeHistoryModal
        open={historyModalShown}
        onClose={closeHistoryModal}
        feeId={activeRowId}
        //
      />

      <DeleteProviderFeeModal
        open={deleteModalShown}
        onClose={closeDeleteModal}
        onDelete={deleteFeeHandler}
        isLoading={isFeeDeleteLoading}
      />

      <CreateProviderFeeModal
        open={createModalShown}
        onClose={closeCreateModal}
        onCreate={createFee}
        fields={editFields}
        fieldsHandler={handleEditFields}
        isLoading={isFeePutLoading}
      />
    </>
  );
};
